import { useSmartNavigation } from "@/components/r3f/controls/smart-navigation/use-smart-navigation";
import { UnknownObject } from "@/object-cache-type-guard";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { useSurfaceWandStore } from "@/tools/surface-wand/surface-wand-context";
import {
  ExplorationControls,
  Map2DControls,
  SphereControls,
} from "@faro-lotv/app-component-toolbox";
import {
  ForwardMovement,
  PointerRotates,
  WalkOrbitControls,
} from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { JSX, useCallback, useEffect, useState } from "react";
import { Vector3 } from "three";
import { FlyWalkAnimation } from "./data-dispatch/flywalk-animation";
import { ImageType } from "./data-dispatch/flywalk-data-dispatch";
import { useToolsAcquaintance } from "./tools/tools-acquaintance-context";

type FlyWalkControlsProps = {
  /** The image type of the current main element */
  imageType: ImageType;
  /** The current animation in flywalk mode */
  animation: FlyWalkAnimation | null;
  /** Controls rotation pivot */
  target: Vector3 | undefined;
  /** Whether a forward translation means walk forward or fly forward*/
  forwardMovement: ForwardMovement | undefined;
  /** Models clickable by controls */
  clickableByControls: UnknownObject[];
  /** True if the SCENE-like smart navigation can be used in this scene */
  smartNavigationAvailable?: boolean;
  /** Callback issued when a new instance of exploration controls is mounted */
  setControls?(controls: WalkOrbitControls | null): void;
  /** Optional callback to set the rotation pivot */
  setTarget?(target: Vector3 | undefined): void;
};

/** @returns The camera controls in fly walk mode */
export function FlyWalkControls({
  imageType,
  animation,
  target,
  forwardMovement,
  clickableByControls,
  smartNavigationAvailable = false,
  setControls,
  setTarget,
}: FlyWalkControlsProps): JSX.Element | null {
  const { onCameraStoppedTranslating, activeToolAndPicking } =
    useToolsAcquaintance();
  const wand = useSurfaceWandStore();
  const mode = useAppSelector(selectModeName);
  const camera = useThree((state) => state.camera);
  useEffect(() => {
    if (
      !wand ||
      mode !== "overview" ||
      imageType !== ImageType.NoImage ||
      !setTarget
    ) {
      return;
    }
    return wand.subscribe((state, previous) => {
      const { request } = previous;
      if (
        request?.cameraId === camera.uuid &&
        state.generation === previous.generation &&
        !state.request &&
        !state.error &&
        state.selection !== previous.selection &&
        state.selection?.cameraId === request.cameraId &&
        state.selection.cloudId === request.cloudId
      ) {
        setTarget(request.point.clone());
      }
    });
  }, [wand, mode, imageType, camera, setTarget]);

  const cameraStopped = useCallback(() => {
    onCameraStoppedTranslating.emit();
  }, [onCameraStoppedTranslating]);

  const [controlsInstance, setControlsInstance] =
    useState<WalkOrbitControls | null>(null);
  const updateControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsInstance(controls);
      setControls?.(controls);
    },
    [setControls],
  );

  useSmartNavigation(
    smartNavigationAvailable &&
      imageType === ImageType.NoImage &&
      !activeToolAndPicking.isPickingToolActive,
    controlsInstance,
    clickableByControls,
    setTarget,
  );

  const enabled = animation === null;

  if (imageType === ImageType.Pano) {
    return <SphereControls isDefault isEnabled={enabled} />;
  } else if (
    imageType === ImageType.Orthomosaic ||
    imageType === ImageType.Photo2D
  ) {
    return <Map2DControls enabled={enabled} />;
  }

  // Suppress the click-to-focus camera re-aim for clicks inside the smart
  // navigation circle, there a click is the start of a look-around instead
  const enableRotationPivot =
    clickableByControls.length > 0 && !activeToolAndPicking.isPickingToolActive;

  return (
    <ExplorationControls
      ref={updateControls}
      enabled={enabled}
      target={target}
      obstacles={[]}
      clickableModels={clickableByControls}
      enableClickToFocus={enableRotationPivot}
      enableDoubleClickToZoom={enableRotationPivot}
      pointerRotates={PointerRotates.ViewDirection}
      forwardMovement={forwardMovement}
      onCameraStoppedTranslating={cameraStopped}
    />
  );
}
